import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import DashLayout from '../../components/DashLayout';

const nav = [
  { to: '/super-admin', icon: '📊', label: 'Dashboard' },
  { to: '/super-admin/businesses', icon: '🏢', label: 'Businesses' },
  { to: '/super-admin/payments', icon: '💰', label: 'Payments' },
  { to: '/super-admin/invoice-report', icon: '🧾', label: 'Invoice Report' },
  { to: '/super-admin/tickets', icon: '🎫', label: 'Support Tickets' },

  { to: '/super-admin/plans', icon: '📦', label: 'Plans' },
  { to: '/super-admin/agents', icon: '👥', label: 'Agent Requests' }
];

const STATUS_COLORS = {
  pending: { bg: 'rgba(245,158,11,0.1)', color: '#f59e0b' },
  pending_verification: { bg: 'rgba(245,158,11,0.1)', color: '#f59e0b' },
  approved: { bg: 'rgba(0,213,91,0.1)', color: '#00d55b' },
  rejected: { bg: 'rgba(255,68,68,0.1)', color: '#ff4444' }
};

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = () => {
    setLoading(true);
    axios.get('/api/payment/pending')
      .then(r => setPayments(r.data.data || []))
      .catch(() => toast.error('Failed to load payments'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try {
      await axios.put(`/api/payment/approve/${id}`);
      toast.success('Payment approved! Business account activated.');
      load();
    } catch { toast.error('Approval failed'); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) return toast.error('Please enter a rejection reason');
    try {
      await axios.put(`/api/payment/reject/${rejectModal.id}`, { reason: rejectReason });
      toast.success('Payment rejected and business notified.');
      setRejectModal(null);
      setRejectReason('');
      load();
    } catch { toast.error('Rejection failed'); }
  };

  const openInvoice = (id) => {
    window.open(`https://api.mschat.msdiscoveries.com/api/payment/invoice/${id}`, '_blank');
  };

  const filtered = filter === 'all' ? payments : payments.filter(p =>
    filter === 'pending' ? ['pending', 'pending_verification'].includes(p.payment_status) : p.payment_status === filter
  );

  const pendingCount = payments.filter(p => ['pending', 'pending_verification'].includes(p.payment_status)).length;

  return (
    <DashLayout navItems={nav} role="Super Admin">
      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Payment Management</h1>
          <p style={{ color: '#555', fontSize: 13, marginTop: 4 }}>Review and approve payment submissions</p>
        </div>

        {/* Reject modal */}
        {rejectModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: '#111', border: '1px solid #ff4444', borderRadius: 16, padding: 28, width: '100%', maxWidth: 420 }}>
              <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 6, color: '#ff4444' }}>Reject Payment</h3>
              <p style={{ color: '#888', fontSize: 13, marginBottom: 16 }}>
                Rejecting payment from <strong style={{ color: '#fff' }}>{rejectModal.company_name}</strong>
              </p>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Reason for rejection *</label>
                <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                  placeholder="e.g. Transaction ID not found, Amount mismatch..."
                  rows={3}
                  style={{ width: '100%', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#fff', padding: '10px 12px', borderRadius: 8, fontFamily: 'inherit', fontSize: 13, resize: 'none' }} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { setRejectModal(null); setRejectReason(''); }}
                  style={{ flex: 1, padding: '10px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                <button onClick={reject}
                  style={{ flex: 2, padding: '10px', background: '#ff4444', color: '#fff', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>Reject Payment</button>
              </div>
            </div>
          </div>
        )}

        {/* Pending alert */}
        {pendingCount > 0 && (
          <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 12, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 20 }}>⏳</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#f59e0b' }}>{pendingCount} payment{pendingCount > 1 ? 's' : ''} waiting for verification</div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Review and approve to activate client accounts</div>
            </div>
          </div>
        )}

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
          {[['pending', '⏳ Pending', pendingCount], ['approved', '✓ Approved', null], ['rejected', '✗ Rejected', null], ['all', 'All', payments.length]].map(([f, l, count]) => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ padding: '7px 16px', borderRadius: 8, border: `1px solid ${filter === f ? '#00d55b' : '#1e1e1e'}`, background: filter === f ? 'rgba(0,213,91,0.1)' : 'transparent', color: filter === f ? '#00d55b' : '#555', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              {l}
              {count !== null && count > 0 && <span style={{ background: filter === f ? '#00d55b' : '#1e1e1e', color: filter === f ? '#000' : '#888', fontSize: 10, padding: '0 5px', borderRadius: 8, fontWeight: 700 }}>{count}</span>}
            </button>
          ))}
        </div>

        {/* Payments table */}
        <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#0d0d0d', borderBottom: '1px solid #1e1e1e' }}>
                {['Business', 'Plan', 'Amount', 'TXN ID', 'Bank', 'Date', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: 11, color: '#555', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#555' }}>Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#333', fontSize: 13 }}>No payments found</td></tr>
              ) : filtered.map(p => {
                const isPending = ['pending', 'pending_verification'].includes(p.payment_status);
                const isApproved = p.payment_status === 'approved';
                const sc = STATUS_COLORS[p.payment_status] || { bg: '#1a1a1a', color: '#555' };
                const pCurr = p.currency === 'usd' ? '$' : '₹';
                return (
                  <tr key={p.id} style={{ borderBottom: '1px solid #0d0d0d' }}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{p.company_name}</div>
                      <div style={{ fontSize: 11, color: '#555' }}>{p.email}</div>
                      <div style={{ fontSize: 11, color: '#555' }}>{p.phone}</div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: 13, color: '#00d55b', fontWeight: 500 }}>{p.plan_name || p.plan_key?.replace(/_/g,' ')}</div>
                      <div style={{ fontSize: 11, color: '#555', textTransform: 'capitalize' }}>{p.billing_cycle}</div>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 15, fontWeight: 700, color: '#fff' }}>
                      {pCurr}{Number(p.amount).toLocaleString('en-IN')}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 12, fontFamily: 'monospace', color: '#888' }}>
                      {p.txn_id}
                      {p.screenshot_url && (
                        <a href={`https://api.mschat.msdiscoveries.com${p.screenshot_url}`} target="_blank" rel="noreferrer"
                          style={{ display: 'block', fontSize: 11, color: '#00d55b', marginTop: 3 }}>📎 View screenshot</a>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: '#888', textTransform: 'capitalize' }}>
                      {p.bank_account === 'india' ? '🇮🇳 India' : '🇺🇸 US'}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: '#555' }}>
                      {new Date(p.submitted_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
                      <div style={{ fontSize: 10, color: '#333', marginTop: 2 }}>
                        {new Date(p.submitted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: sc.bg, color: sc.color, fontWeight: 600, textTransform: 'capitalize' }}>
                        {p.payment_status?.replace(/_/g,' ')}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        {isPending && (
                          <>
                            <button onClick={() => approve(p.id)}
                              style={{ padding: '5px 12px', background: 'rgba(0,213,91,0.1)', border: '1px solid #00d55b', color: '#00d55b', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>
                              ✓ Approve
                            </button>
                            <button onClick={() => setRejectModal(p)}
                              style={{ padding: '5px 12px', background: 'rgba(255,68,68,0.08)', border: '1px solid #ff4444', color: '#ff4444', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>
                              ✗ Reject
                            </button>
                          </>
                        )}
                        {isApproved && (
                          <button onClick={() => openInvoice(p.id)}
                            style={{ padding: '5px 12px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#888', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>
                            🧾 Invoice
                          </button>
                        )}
                        {p.rejection_reason && (
                          <div style={{ fontSize: 10, color: '#ff4444', maxWidth: 120 }} title={p.rejection_reason}>
                            Reason: {p.rejection_reason.substring(0, 30)}...
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DashLayout>
  );
}